import { useState } from "react";
import { useDraggable } from "@dnd-kit/core";
import styled from "styled-components";
import IconButton from "@mui/material/IconButton";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { AvailabilityModal } from "./AvailabilityModal";

export interface DraggableProduct {
    productId: string;
    tdmsProductId: number;
    name: string;
    productImagePath?: string;
    productClass?: string | null;
    durationDays?: number;
    rrp: number;
    fareTypeId: string | number;
    fareName?: string;
    productPricesDetailsId: string | number;
    numPax?: number;
    /**
     * Live-rate booking keys for externally-sourced accommodation rooms
     * (e.g. Hotelbeds). Not displayed — carried through to the checkout
     * payload so the room can be booked. Absent for CMS/generic suppliers
     * and for non-accommodation products.
     */
    defaultRateCode?: string;
    roomCode?: string;
    externalId?: string;
    externalSource?: string;
}

const Card = styled.div<{ $dragging: boolean }>`
    display: flex;
    gap: 10px;
    padding: 8px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    background: #fff;
    user-select: none;
    box-shadow: ${(p) => (p.$dragging ? "0 6px 18px rgba(0,0,0,.18)" : "none")};
    opacity: ${(p) => (p.$dragging ? 0.85 : 1)};
    position: relative;
    &:hover { border-color: #bdbdbd; }
`;

const DragHandle = styled.div<{ $tappable?: boolean }>`
    display: flex;
    gap: 10px;
    flex: 1;
    min-width: 0;
    cursor: ${(p) => (p.$tappable ? "pointer" : "grab")};
`;

const LeftCol = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
`;

const Thumb = styled.div<{ $src?: string }>`
    width: 56px;
    height: 56px;
    border-radius: 4px;
    background: #eee center/cover no-repeat ${(p) => (p.$src ? `url(${p.$src})` : "")};
    flex-shrink: 0;
`;

const Body = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
    justify-content: center;
`;

const Title = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: #1a1a1a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding-right: 24px;
`;

const Duration = styled.div`
    font-size: 12px;
    color: #757575;
    margin-top: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Fare = styled.div`
    font-size: 11px;
    color: #1976d2;
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding-right: 24px;
`;

const Price = styled.div`
    font-size: 12px;
    color: #d32f2f;
    font-weight: 600;
    white-space: nowrap;
    text-align: center;
`;

const formatDuration = (days?: number) => {
    if (!days || days <= 1) return "1 day";
    const nights = days - 1;
    return `${days} day${days === 1 ? "" : "s"} ${nights} night${nights === 1 ? "" : "s"}`;
};

interface Props {
    product: DraggableProduct;
    /**
     * If provided, tapping the card body invokes this callback instead of (or
     * in addition to, on touch) initiating a drag. Used by the mobile bottom
     * sheet to open an "Add to which day?" picker — see TripPlanner index.
     * Drag listeners remain attached so a long-press drag still works for
     * users who prefer it.
     */
    onSelect?: (product: DraggableProduct) => void;
}

export const ProductCard = ({ product, onSelect }: Props) => {
    const [modalOpen, setModalOpen] = useState(false);
    const { attributes, listeners, setNodeRef, isDragging, transform } = useDraggable({
        id: `product:${product.productId}`,
        data: { kind: "product", product },
    });
    const style = transform
        ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
        : undefined;
    // The info button must NOT be inside the drag-handle subtree, otherwise
    // dnd-kit's PointerSensor can swallow the click. Stopping propagation on
    // pointerdown belt-and-braces protects it if it ever moves.
    return (
        <>
            <Card ref={setNodeRef} $dragging={isDragging} style={style}>
                <DragHandle
                    {...listeners}
                    {...attributes}
                    $tappable={!!onSelect}
                    role={onSelect ? "button" : undefined}
                    aria-label={onSelect ? `Add ${product.name} to a day` : undefined}
                    onClick={onSelect ? () => onSelect(product) : undefined}
                >
                    <LeftCol>
                        <Thumb $src={product.productImagePath} />
                        <Price>From: ${Number(product.rrp).toFixed(0)}</Price>
                    </LeftCol>
                    <Body>
                        <Title title={product.name}>{product.name}</Title>
                        <Duration>{formatDuration(product.durationDays)}</Duration>
                        {product.fareName && (
                            <Fare title={`Default fare: ${product.fareName}`}>
                                Fare: {product.fareName}
                            </Fare>
                        )}
                    </Body>
                </DragHandle>
                <IconButton
                    size="small"
                    aria-label="View availability"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                        e.stopPropagation();
                        setModalOpen(true);
                    }}
                    sx={{ position: "absolute", top: 4, right: 4, p: 0.5 }}
                >
                    <CalendarMonthIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Card>
            {modalOpen && (
                <AvailabilityModal
                    open={modalOpen}
                    productId={product.productId}
                    productName={product.name}
                    productClassOverride={product.productClass}
                    onClose={() => setModalOpen(false)}
                />
            )}
        </>
    );
};
